/**
 * Lightweight, privacy-friendly website analytics for B&B Emmanuel.
 *
 * Fires events to backend `/api/track`. The session id is stored in
 * sessionStorage so it survives client-side navigation but resets on tab
 * close. No PII is sent.
 */
import axios from "axios";
import { getToken } from "@/lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "bb_track_session";
const SEEN_PAGES_KEY = "bb_track_pages_seen";
const ADMIN_OPTOUT_KEY = "bb_admin_optout"; // persists across sessions

/** Returns true if the current device should be EXCLUDED from analytics.
 *  Excluded when:
 *   - There is (or has ever been) a valid admin token on this device, OR
 *   - The user manually opted out via `?noanalytics=1`.
 */
const isAdminDevice = () => {
  try {
    // 1) Active admin session right now → never count
    if (getToken()) {
      localStorage.setItem(ADMIN_OPTOUT_KEY, "1");
      return true;
    }
    // 2) Sticky opt-out flag (set the first time the admin ever logged in
    //    on this device — survives logout so we never count the owner)
    if (localStorage.getItem(ADMIN_OPTOUT_KEY) === "1") return true;
    // 3) Manual escape hatch: append ?noanalytics=1 once and we remember it
    const qp = new URLSearchParams(window.location.search);
    if (qp.get("noanalytics") === "1") {
      localStorage.setItem(ADMIN_OPTOUT_KEY, "1");
      return true;
    }
  } catch { /* ignore */ }
  return false;
};

const getSession = () => {
  let s = "";
  try {
    s = sessionStorage.getItem(SESSION_KEY) || "";
  } catch { /* sessionStorage disabled */ }
  if (!s) {
    s = "s-" + Math.random().toString(36).slice(2, 10) + "-" + Date.now().toString(36);
    try { sessionStorage.setItem(SESSION_KEY, s); } catch { /* ignore */ }
  }
  return s;
};

const send = async (event, extra = {}) => {
  if (!BACKEND_URL) return;
  if (isAdminDevice()) return; // never count the admin's own visits
  try {
    await axios.post(
      `${BACKEND_URL}/api/track`,
      {
        event,
        session_id: getSession(),
        page: typeof window !== "undefined" ? window.location.pathname : "",
        referrer: typeof document !== "undefined" ? document.referrer : "",
        utm_source: (() => {
          try {
            return new URLSearchParams(window.location.search).get("utm_source") || "";
          } catch { return ""; }
        })(),
        ...extra,
      },
      { timeout: 4000 }
    );
  } catch (e) {
    // never bubble analytics errors to the user
    // eslint-disable-next-line no-console
    console.debug("analytics track failed", event, e?.message);
  }
};

export const trackPageView = () => {
  // Deduplicate page_view per (session, path) to avoid double counting on
  // strict-mode mounts or rapid back/forward.
  if (typeof window === "undefined") return;
  const path = window.location.pathname;
  let seen = [];
  try {
    seen = JSON.parse(sessionStorage.getItem(SEEN_PAGES_KEY) || "[]");
  } catch { /* ignore */ }
  if (seen.includes(path)) return;
  seen.push(path);
  try { sessionStorage.setItem(SEEN_PAGES_KEY, JSON.stringify(seen.slice(-30))); } catch { /* ignore */ }
  send("page_view");
};

export const trackChatOpen = () => send("chat_open");

export const trackBookingStart = () => send("booking_start");

export const trackBookingCompleted = (bookingCode) =>
  send("booking_completed", { booking_code: bookingCode || "" });
